import readline from 'node:readline';
import { logger } from '../utils/logger.js';
import {
  loadBootstrapState,
  persistBootstrapState,
  type BootstrapSessionState,
} from '../utils/bootstrap-state.js';
import { syncProjectAuthEnv } from '../utils/project-auth-env.js';
import { exchangeBootstrapCode, parseBootstrapCodeInput, resolvePlatformEndpoint } from './init.js';
import {
  cancelCliAuthSession,
  openBrowser,
  resolvePlatformAuthBrowserUrl,
  startCliAuthSession,
  waitForCliAuthCompletion,
} from '../utils/cli-auth-api.js';

export interface LoginOptions {
  bootstrap?: string;
  endpoint?: string;
  open?: boolean;
  exitOnError?: boolean;
}

async function prompt(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

export async function loginWithBootstrap(
  projectPath: string,
  options: LoginOptions,
  fetchImpl: typeof fetch = fetch,
): Promise<{ state: BootstrapSessionState; reused: boolean }> {
  const endpoint = resolvePlatformEndpoint({ endpoint: options.endpoint });
  const existing = loadBootstrapState(projectPath);

  if (existing && existing.endpoint === endpoint && existing.session_cookie && !options.bootstrap?.trim()) {
    syncProjectAuthEnv(projectPath, endpoint, existing);
    return { state: existing, reused: true };
  }

  const bootstrapCode = options.bootstrap?.trim();
  if (!bootstrapCode) {
    if (existing && existing.endpoint === endpoint && !existing.session_cookie) {
      throw new Error(
        'Stored org session is missing credentials. Re-authenticate with: npx @standardagents/cli login --bootstrap <code>',
      );
    }
    throw new Error('Bootstrap code required. Run: npx @standardagents/cli login --bootstrap <code>');
  }

  const state = await consumeBootstrapLogin({ ...options, endpoint, bootstrap: bootstrapCode }, fetchImpl);
  persistBootstrapState(projectPath, state);
  syncProjectAuthEnv(projectPath, endpoint, state);
  return { state, reused: false };
}

export async function consumeBootstrapLogin(
  options: LoginOptions,
  fetchImpl: typeof fetch = fetch,
): Promise<BootstrapSessionState> {
  const endpoint = resolvePlatformEndpoint({ endpoint: options.endpoint });
  const bootstrapCode = options.bootstrap?.trim();
  if (!bootstrapCode) {
    throw new Error('Bootstrap code required. Run: npx @standardagents/cli login --bootstrap <code>');
  }

  const consumed = await exchangeBootstrapCode(endpoint, bootstrapCode, fetchImpl);
  return {
    version: 1,
    endpoint,
    authenticated_at: Date.now(),
    session_cookie: consumed.session_cookie,
    user: consumed.user,
    account: consumed.account,
  };
}

async function loginWithPlatform(options: LoginOptions): Promise<void> {
  const endpoint = resolvePlatformEndpoint({ endpoint: options.endpoint });
  let authAppUrl = resolvePlatformAuthBrowserUrl(endpoint);
  let sessionId: string | null = null;
  let wsUrl: string | null = null;
  let wsExpiresAt = 0;

  logger.log('');
  logger.info('Starting StandardAgents CLI authentication...');
  logger.log(`Platform API: ${endpoint}`);

  try {
    const started = await startCliAuthSession(endpoint);
    sessionId = started.session_id;
    authAppUrl = started.auth_url;
    wsUrl = started.ws_url;
    wsExpiresAt = started.expires_at;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to start realtime auth session';
    logger.warning(`${message} Switching to manual bootstrap command entry.`);
  }

  logger.log(`Auth app: ${authAppUrl}`);
  logger.log('');
  logger.log('Complete login/signup in your browser. This window is waiting for realtime confirmation...');

  if (options.open !== false) {
    const opened = openBrowser(authAppUrl);
    if (opened) {
      logger.info('Opened browser for authentication.');
    } else {
      logger.warning('Could not automatically open browser. Open the URL above manually.');
    }
  }

  if (!process.stdin.isTTY) {
    const message = 'Non-interactive shell detected. Run: npx @standardagents/cli login --bootstrap <code>';
    if (options.exitOnError === false) {
      throw new Error(message);
    }
    logger.error(message);
    process.exit(1);
  }

  if (wsUrl && wsExpiresAt > 0) {
    try {
      const completion = await waitForCliAuthCompletion(wsUrl, wsExpiresAt);
      const projectPath = process.cwd();
      const { state, reused } = await loginWithBootstrap(
        projectPath,
        { ...options, endpoint, bootstrap: completion.bootstrapCode },
      );

      logger.log('');
      if (reused) {
        logger.success('Already authenticated in this project.');
      } else {
        logger.success('Bootstrap login successful.');
      }
      logger.log(`Account: ${state.account.id}${state.account.slug ? ` (${state.account.slug})` : ''}`);
      logger.log(`User: ${state.user.email}`);
      logger.log('');
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Realtime auth handoff failed';
      logger.warning(`${message} Enter bootstrap code manually to continue.`);
    }
  }

  logger.log('');
  const input = await prompt('Bootstrap code or command: ');
  const bootstrapCode = parseBootstrapCodeInput(input);
  if (!bootstrapCode) {
    if (sessionId) {
      await cancelCliAuthSession(endpoint, sessionId);
    }
    const message = 'No bootstrap code provided. Run: npx @standardagents/cli login --bootstrap <code>';
    if (options.exitOnError === false) {
      throw new Error(message);
    }
    logger.error(message);
    process.exit(1);
  }

  if (sessionId) {
    await cancelCliAuthSession(endpoint, sessionId);
  }
  const projectPath = process.cwd();
  const { state, reused } = await loginWithBootstrap(projectPath, { ...options, endpoint, bootstrap: bootstrapCode });
  logger.log('');
  if (reused) {
    logger.success('Already authenticated in this project.');
  } else {
    logger.success('Bootstrap login successful.');
  }
  logger.log(`Account: ${state.account.id}${state.account.slug ? ` (${state.account.slug})` : ''}`);
  logger.log(`User: ${state.user.email}`);
  logger.log('');
}

export async function login(options: LoginOptions = {}): Promise<void> {
  const bootstrapCode = options.bootstrap?.trim();
  if (bootstrapCode) {
    const projectPath = process.cwd();
    try {
      const { state, reused } = await loginWithBootstrap(projectPath, options);
      logger.log('');
      if (reused) {
        logger.success('Already authenticated in this project.');
      } else {
        logger.success('Bootstrap login successful.');
      }
      logger.log(`Account: ${state.account.id}${state.account.slug ? ` (${state.account.slug})` : ''}`);
      logger.log(`User: ${state.user.email}`);
      logger.log('');
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      if (options.exitOnError === false) {
        throw new Error(message);
      }
      logger.error(message);
      process.exit(1);
    }
  }

  await loginWithPlatform(options);
}
